import {
  drawGuides,
  getAnchorPoints,
  getGuides,
  getLineGuideStops,
} from 'dataloader/utils/guideLines'
import Konva from 'konva'
import { round } from 'lodash'
import React from 'react'
import { Circle, Group, Rect } from 'react-konva'
import { connect } from 'react-redux'
import action from 'store/actions'
import { incrementMaterialPresets } from 'store/materialPresets/actions'
import {
  getAutoIncrementPresetsEnabled,
  getDescriptions,
  getDescriptionsData,
  getDrawings,
  getMaterialTypePresets,
  getSnappingDistance,
  getSnappingEnabled,
} from 'store/selectors'
import randomId from 'utils/randomId'
import { throttleRaf } from 'utils/throttleRaf'
import withSelectedValues, { DataloaderInfo } from 'utils/withSelectedValues'

interface OwnProps {
  width: number
  height: number
  scale: { x: number; y: number }
}

interface StateProps {
  descriptions: string[]
  descriptionsData: ReduxStore.Materials.Data.DescriptionData[]
  drawings: ReduxStore.Drawings.Data.Drawing[]
  snappingEnabled: boolean
  presets: ReduxStore.MaterialPresets.MaterialPreset[]
  autoIncrementEnabled: boolean
  snappingDistance: number
  options: any
}

interface DispatchProps {
  dispatchAddMaterials: (m: ReduxStore.Materials.Data.IMaterial) => void
  incrementPresets: (materialTypeId: number) => void
}

type Props = OwnProps & StateProps & DispatchProps & DataloaderInfo

interface PointsPos {
  x: number
  y: number
}

const PointTool: React.FunctionComponent<Props> = props => {
  const {
    width,
    height,
    descriptions,
    dispatchAddMaterials,
    incrementPresets,
    selectedDrawing,
    selectedProject,
    selectedMaterialType,
    drawings,
    scale,
    autoIncrementEnabled,
    options
  } = props

  // Safely extract the radius from options
  const pointRadius = options && typeof options.radius === 'number' ? options.radius : 25

  const getObjectSnappingEdges = (evt: Konva.KonvaEventObject<MouseEvent>) => {
    const pos = eventToCoordinates(evt)
    if (pos.x && pos.y) {
      return {
        vertical: [
          {
            guide: Math.round(pos.x),
            offset: 0,
            snap: 'start',
          },
        ],
        horizontal: [
          {
            guide: Math.round(pos.y),
            offset: 0,
            snap: 'start',
          },
        ],
      }
    }
  }

  const handleMouseDown = (evt: Konva.KonvaEventObject<MouseEvent>) => {
    // Ignore middle mouse button (button = 1) for panning
    if (evt.evt.button === 1) {
      // Let the event propagate to allow for panning
      return;
    }
    
    evt.evt.stopPropagation()
    const pos: PointsPos = { ...eventToCoordinates(evt) }
    
    // Apply snapping if enabled
    if (props.snappingEnabled) {
      const lineGuideStops = getLineGuideStops(evt.target, pos, props.snappingDistance)
      const itemBounds = getObjectSnappingEdges(evt)
      if (itemBounds) {
        const guides = getGuides(lineGuideStops, itemBounds)
        const { presetX, presetY } = getAnchorPoints(guides)
        pos.x = presetX || pos.x
        pos.y = presetY || pos.y
      }
    }
    
    if (autoIncrementEnabled) {
      incrementPresets(selectedMaterialType as number)
    }
    
    // Create a new point at the clicked position
    createNewPoint(randomId(), pos)
  }

  const handleMouseMove = throttleRaf((evt: Konva.KonvaEventObject<MouseEvent>) => {
    // Skip processing for middle mouse button events
    if (evt.evt.buttons === 4) { // 4 corresponds to middle button being pressed
      return;
    }
    
    // @ts-ignore
    if (
      evt.target.findAncestor('Stage') !== undefined &&
      evt.target.getStage()!.getPointerPosition() !== undefined
    ) {
      evt.target
        .getLayer()
        .find('.guid-line')
        .each((node: Konva.Node) => node.destroy())

      if (props.snappingEnabled) {
        const pos = { ...eventToCoordinates(evt) }
        const lineGuideStops = getLineGuideStops(evt.target, pos, props.snappingDistance)
        const itemBounds = getObjectSnappingEdges(evt)
        if (itemBounds) {
          const guides = getGuides(lineGuideStops, itemBounds)
          drawGuides(guides, evt.target)
        }
      }
    }
  })

  const eventToCoordinates = (evt: Konva.KonvaEventObject<MouseEvent>): PointsPos => {
    const transform = evt.target.getAbsoluteTransform().copy()
    transform.invert()
    const stage = evt.target.findAncestor('Stage')
    // @ts-ignore
    const pos = transform.point(stage.getPointerPosition())
    return { x: round(pos.x, 2), y: round(pos.y, 2) }
  }

  const createNewPoint = (overlay_id: string, pos: PointsPos) => {
    dispatchAddMaterials({
      overlay_id,
      material_id: overlay_id,
      drawing_id: drawings.filter(drawing => drawing.displayName === selectedDrawing)[0].id,
      display_name: selectedDrawing as string,
      shape: 'point',
      shape_data: JSON.stringify({
        position: [pos.x, pos.y],
        radius: pointRadius // Use the radius from options or default
      }),
      company_project_id: selectedProject as number,
      material_type_id: selectedMaterialType as number,
      value: descriptions.reduce((acc: { [key: string]: string }, name: string) => {
        if (selectedDrawing && name === 'Drawing Name') {
          acc[name] = selectedDrawing
        } else {
          // Check if there is a preset set for this.
          const preset = props.presets.find(preset => preset.description === name)
          if (preset) {
            acc[name] = preset.value
          } else {
            const descriptionData = props.descriptionsData.find(
              descData => descData.description === name
            )
            if (
              descriptionData &&
              (descriptionData.type === 'list' || descriptionData.type === 'button-list') &&
              descriptionData.choices.length > 0
            ) {
              acc[name] = descriptionData.choices[0]
            } else {
              acc[name] = ''
            }
          }
        }
        return acc
      }, {}),
    })
  }

  return (
    <Group onMouseMove={handleMouseMove}>
      <Rect
        x={0}
        y={0}
        width={width}
        height={height}
        fill="rgba(0, 0, 0, 0)"
        onMouseDown={handleMouseDown}
      />
    </Group>
  )
}

const mapStateToProps = (
  state: ReduxStore.State,
  { selectedMaterialType }: { selectedMaterialType: string }
) => {
  const pointOptions = state.tool.options['point'] || { radius: 25 };

  return {
    descriptions: getDescriptions(state),
    descriptionsData: getDescriptionsData(state),
    drawings: getDrawings(state),
    snappingEnabled: getSnappingEnabled(state),
    presets: getMaterialTypePresets(state, selectedMaterialType),
    autoIncrementEnabled: getAutoIncrementPresetsEnabled(state),
    snappingDistance: getSnappingDistance(state),
    options: pointOptions
  };
}

const mapDispatchToProps = (dispatch: any) => ({
  dispatchAddMaterials: (m: ReduxStore.Materials.Data.IMaterial) => dispatch(action.addMaterial(m)),
  incrementPresets: (materialTypeId: number) => dispatch(incrementMaterialPresets(materialTypeId)),
})

export default withSelectedValues(
  // @ts-ignore
  connect(
    mapStateToProps,
    mapDispatchToProps
  )(PointTool)
)